/**
 * GateFixPanel — named blockers + in-place quick fixes on Review & submit.
 *
 * Bill 2026-07-23 (Peter's-cart post-mortem): the blocked banner said
 * "resolve each blocker in its source step" but never NAMED the blockers —
 * the FSR stared at all-green chips while MISSING_DOB silently held commit.
 * This panel resurrects per-gate naming (the orphaned GateReport's job) and
 * goes one further: "any surfaced gating chip … allow for a quick targeted
 * item pop-up modal to satisfy the gating" — no round-tripping to People.
 *
 * Row sources (all from the SAME shared spec the engine + person modal use):
 *  1. Per-person completeness — missingRequiredIndividualFields() on every
 *     NEW person (existing-CIF exempt, mirrors server) → [Quick fix] opens
 *     GateQuickFixModal with ONLY the missing fields.
 *  2. Per-person OFAC — NEW person without a satisfactory screen →
 *     [Run OFAC now] fires the same headless scan the PdwShell auto-fire
 *     uses; an active hit routes to [Open person →] (full Verify workflow).
 *  3. Remaining engine blockers (minus the MISSING_* + OFAC codes rows 1-2
 *     already cover) — named with reason; person-scoped ones get
 *     [Open person →].
 */
import { useMemo, useState } from 'react';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import type { GateReport as GateReportData, GateResult } from '@/services/workflow-engine/types';
import {
  REQUIRED_INDIVIDUAL_FIELDS,
  missingRequiredIndividualFields,
  type RequiredIndividualField,
} from '@/services/rules/requiredIndividualFields';
import type { RaoPerson } from '../../types';
import { isExistingCustomer } from '../personDialog/shared/existingCustomer';
import { hasFreshSatisfactoryOfac } from '../personDialog/shared/ofacAutoScan';
import { normalizeOfacStatus } from '../personDialog/shared/ofacStatus';

// ---------------------------------------------------------------------------
// Shared leaf styles (house pattern — lifted from the person-modal sections)
// ---------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 13,
  border: '1px solid var(--ink-300, #cbd2d9)',
  borderRadius: 6,
  boxSizing: 'border-box',
};

function personLabel(p: RaoPerson): string {
  const nm = (p.fullName || `${p.firstName ?? ''} ${p.lastName ?? ''}`).trim();
  return nm || 'Unnamed person';
}

function personKey(p: RaoPerson, i: number): string {
  return p.id ?? p.cifId ?? `${p.fullName ?? 'person'}-${i}`;
}

// ---------------------------------------------------------------------------
// GateQuickFixModal — micro-modal for a single person's missing fields
// ---------------------------------------------------------------------------

export interface GateQuickFixModalProps {
  open: boolean;
  person: RaoPerson;
  onClose: () => void;
  /** Persist the patched person (parent runs the per-party PATCH → refresh). */
  onSave: (updated: RaoPerson) => void | Promise<void>;
  saving?: boolean;
  /** Persist error surfaced by the parent (PATCH failure). */
  error?: string | null;
}

/** Input flavor per field — dates get a date picker, the rest stay text. */
function inputTypeFor(field: RequiredIndividualField['field']): string {
  return field === 'dob' ? 'date' : 'text';
}

export function GateQuickFixModal({
  open,
  person,
  onClose,
  onSave,
  saving,
  error,
}: GateQuickFixModalProps): JSX.Element | null {
  // The fields that were missing WHEN THE MODAL OPENED — values start empty
  // by definition (that's why the row exists), so nothing sensitive is
  // echoed here.
  const missingSpecs = useMemo(
    () =>
      REQUIRED_INDIVIDUAL_FIELDS.filter((spec) => {
        const v = (person as unknown as Record<string, unknown>)[spec.field];
        return typeof v !== 'string' || v.trim().length === 0;
      }),
    [person],
  );
  const [values, setValues] = useState<Record<string, string>>({});

  if (!open) return null;

  const allFilled = missingSpecs.every((s) => (values[s.field] ?? '').trim().length > 0);

  const handleSave = (): void => {
    const patch: Record<string, string> = {};
    for (const s of missingSpecs) {
      const v = (values[s.field] ?? '').trim();
      if (v) patch[s.field] = s.field === 'state' ? v.toUpperCase() : v;
    }
    void onSave({ ...person, ...patch } as RaoPerson);
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={`Quick fix — ${personLabel(person)}`}
      size="sm"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={!!saving}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!allFilled || !!saving}
            title={allFilled ? undefined : 'Fill every field to save'}
          >
            {saving ? 'Saving…' : 'Save & re-check'}
          </Button>
        </div>
      }
    >
      <p style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 'var(--sp-4)' }}>
        Only the missing required items are shown — everything else on this
        person is untouched. Saving re-checks the gates immediately.
      </p>
      {error && (
        <div style={{ marginBottom: 'var(--sp-3)' }}>
          <Banner tone="err">{error}</Banner>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-4)' }}>
        {missingSpecs.map((s) => (
          <label key={s.field}>
            <span style={labelStyle}>{s.label}</span>
            <input
              type={inputTypeFor(s.field)}
              style={inputStyle}
              value={values[s.field] ?? ''}
              maxLength={s.field === 'state' ? 2 : s.field === 'zip' ? 10 : undefined}
              onChange={(e) => setValues((prev) => ({ ...prev, [s.field]: e.target.value }))}
              autoComplete="off"
            />
          </label>
        ))}
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------------------
// GateBlockerPanel — the named-blocker list with targeted CTAs
// ---------------------------------------------------------------------------

export interface GateBlockerPanelProps {
  report: GateReportData;
  people: ReadonlyArray<RaoPerson>;
  cartEditable: boolean;
  /** SystemSetting risk.ofac.rescreenStaleAfterDays (shared with the server). */
  ofacStaleDays: number;
  onQuickFix: (person: RaoPerson) => void;
  onRunOfac: (person: RaoPerson) => void;
  onOpenPerson: (person: RaoPerson) => void;
  /** personKey currently running an OFAC screen (disables that row's button). */
  runningOfacKey?: string | null;
}

/** Engine gate codes rows 1-2 already cover — suppress from row-3 to avoid
 *  double-listing the same defect. */
function coveredByPersonRows(g: GateResult): boolean {
  return g.gateCode === 'OFAC'
    || g.gateCode.startsWith('MISSING_')
    || g.gateCode.startsWith('JOINT_MISSING_');
}

/** Person-scoped engine gates where "open the primary person" is the right
 *  remediation surface. */
const PRIMARY_PERSON_GATES: ReadonlySet<string> = new Set([
  'CIP', 'CDD', 'NONCIT_COUNTRY', 'VISA_VALIDATION',
]);

export function GateBlockerPanel({
  report,
  people,
  cartEditable,
  ofacStaleDays,
  onQuickFix,
  onRunOfac,
  onOpenPerson,
  runningOfacKey,
}: GateBlockerPanelProps): JSX.Element | null {
  const completenessRows = useMemo(
    () =>
      people
        .map((p, i) => ({ p, i, missing: isExistingCustomer(p) ? [] : missingRequiredIndividualFields(p) }))
        .filter((r) => r.missing.length > 0),
    [people],
  );

  const ofacRows = useMemo(
    () =>
      people
        .map((p, i) => ({ p, i, status: normalizeOfacStatus((p.extra as Record<string, unknown> | undefined)?.['ofacStatus'] as string | undefined) }))
        .filter(({ p }) => !isExistingCustomer(p) && !hasFreshSatisfactoryOfac(p, ofacStaleDays)),
    [people, ofacStaleDays],
  );

  const engineRows = useMemo(
    () =>
      report.gates.filter(
        (g) => (g.status === 'fail' || g.status === 'blocked') && !coveredByPersonRows(g),
      ),
    [report.gates],
  );

  if (completenessRows.length === 0 && ofacRows.length === 0 && engineRows.length === 0) {
    return null;
  }

  const primary = people[0];

  const row = (
    key: string,
    label: string,
    detail: string,
    cta: JSX.Element | null,
  ): JSX.Element => (
    <li
      key={key}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderTop: '1px solid var(--ink-200, var(--surface-border))',
      }}
    >
      <Pill variant="err">Blocked</Pill>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-900, #1d1d1f)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-700, #4b5563)' }}>{detail}</div>
      </div>
      {cta}
    </li>
  );

  return (
    <div
      role="region"
      aria-label="What is blocking approval"
      style={{
        border: '1px solid var(--banner-err-border, #b10505)',
        borderRadius: 8,
        overflow: 'hidden',
        background: 'var(--surface-card, #fff)',
      }}
    >
      <div
        style={{
          padding: '8px 12px',
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--pcb-red, #b10505)',
          background: 'var(--banner-err-bg, #fdecec)',
        }}
      >
        What&apos;s blocking approval
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {completenessRows.map(({ p, i, missing }) =>
          row(
            `complete-${personKey(p, i)}`,
            `${personLabel(p)} — missing required information`,
            `Missing: ${missing.join(', ')}.`,
            cartEditable ? (
              <Button variant="ghost" size="sm" onClick={() => onQuickFix(p)}>
                Quick fix
              </Button>
            ) : null,
          ),
        )}
        {ofacRows.map(({ p, i, status }) => {
          const key = personKey(p, i);
          const isHit = status === 'hit-current';
          return row(
            `ofac-${key}`,
            `${personLabel(p)} — OFAC screening ${isHit ? 'matched an active sanction' : 'not complete'}`,
            isHit
              ? 'Active sanctions match — open the person to adjudicate (override with a compensating document if it is a false positive).'
              : 'No satisfactory OFAC screen on record for this new customer.',
            !cartEditable ? null : isHit ? (
              <Button variant="ghost" size="sm" onClick={() => onOpenPerson(p)}>
                Open person →
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRunOfac(p)}
                disabled={runningOfacKey === key}
              >
                {runningOfacKey === key ? 'Running…' : 'Run OFAC now'}
              </Button>
            ),
          );
        })}
        {engineRows.map((g) =>
          row(
            `gate-${g.gateCode}`,
            g.label,
            g.reason ?? 'This gate has not passed yet.',
            cartEditable && primary && PRIMARY_PERSON_GATES.has(g.gateCode) ? (
              <Button variant="ghost" size="sm" onClick={() => onOpenPerson(primary)}>
                Open person →
              </Button>
            ) : null,
          ),
        )}
      </ul>
    </div>
  );
}
